import {
  Alert,
  Button,
  FormControl,
  FormErrorMessage,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Switch,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { BaseParamsDto, GeneralParamsDto, InsuranceParamsTravelDto, PolicyHolderDto, TravelDto } from 'BrokerTsLib/src/dtos'
import { InitialGeneralParamsValues, InitialPersonAdditionalData, InitialPolicyHolderValues, InitialTravelAddonValues, InitialTravelValues } from 'BrokerTsLib/src/dtos'
import { Risk, Selection } from 'brokertslib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { Controller, FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { getFormWarnings } from 'BrokerTsLib/src/Utils/formWarnings'
import { BaseParamsValidationRuleObject, PolicyHolderTravelValidationSchema, TravelValidationSchema } from 'brokertslib/src/validation'
import _ from 'lodash'
import moment from 'moment'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'

import { useGetWarningsTravel } from '@/hooks/useWizard'

import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { Datepicker } from '../../components/Datepicker'
import ErrorSummary from '../../components/ErrorSummary'
import { FormGroup } from '../../components/FormGroup'
import { FormLabel } from '../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { FormSpacer } from '../../components/FormSpacer'
import { InputGroup } from '../../components/InputGroup'
import { InsuranceCompanies } from '../../components/InsuranceCompanies'
import { MainContent } from '../../components/MainContent'
import { RadioStack } from '../../components/RadioStack'
import { SwitchControl } from '../../components/SwitchControl'
import { useGetRiskVariants, useInsurers } from '../../hooks'
import { useNameof } from '../../hooks/useNameof'
import { useGetBirthdate } from '../../hooks/useUser'
import { Check } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { transformDate } from '../../utils/formatDate'
import { BCNOptions } from '../BCNOptions'
import { FormButtons } from '../FormButtons'
import { ICOOptions } from '../ICOOptions'
import { InsureeName } from '../InsureeName'
import { InsureeType } from '../InsureeType'
import { RegionSelector } from '../RegionSelector'
import { AdditionalInsurees } from './AdditionalInsurees'
import { DestinationSelector } from './DestinationSelector'
import { TripKind } from './TripKind'
import { TripSportType } from './TripSportType'
import { TripType } from './TripType'

interface Step1FormProps {
  onSubmit: (values: InsuranceParamsTravelDto) => Promise<any>,
  data?: InsuranceParamsTravelDto
}

export const schema = yup.object().shape(
  {
    travel: TravelValidationSchema,
    policyHolder: PolicyHolderTravelValidationSchema,
    baseParams: yup.object().shape(BaseParamsValidationRuleObject) as any
  }
)

const defaultValues: InsuranceParamsTravelDto = {
  nextPage: 'next',
  generalParams: InitialGeneralParamsValues,
  policyHolder: InitialPolicyHolderValues,
  travel: InitialTravelValues,
  addons: InitialTravelAddonValues
}

export const transformInsuranceParamsToFormValues = (values: InsuranceParamsTravelDto) => {
  return {
    ...values,
    generalParams: transformGeneralParamsDto(values.generalParams),
    policyHolder: transformPolicyHolderDto(values.policyHolder),
    travel: transformTravelDto(values.travel)
  } as InsuranceParamsTravelDto
}

export const transformPolicyHolderDto = (policyHolder: PolicyHolderDto) => {
  if (policyHolder.person == null) policyHolder.person = { ...InitialPersonAdditionalData }
  if (policyHolder.owner == null) policyHolder.owner = { ...InitialPersonAdditionalData }
  if (policyHolder.holder == null) policyHolder.holder = { ...InitialPersonAdditionalData }

  return policyHolder
}

export const transformGeneralParamsDto = (generalParams: GeneralParamsDto) => {
  generalParams.insuranceStartDate = transformDate(generalParams.insuranceStartDate)

  return generalParams
}

export const transformTravelDto = (travel: TravelDto) => {
  travel.insuranceEndDate = transformDate(travel.insuranceEndDate)

  return travel
}

export const Step1Form = ({ onSubmit, data = defaultValues }: Step1FormProps) => {
  const { triggerNewInsurance, clearTriggerNewInsurance, refetchInsuranceParams } = useContext(insuranceParamsContext)

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const [tripEndDateOpened, setTripEndDateOpened] = useState(false)
  const tripStartDateRef = useRef<any>(null)
  const tripEndDateRef = useRef<any>(null)

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const formValues = useMemo(() => transformInsuranceParamsToFormValues(data), [data])
  useEffect(() => {
    methods.reset(formValues)
  }, [formValues])

  const isContracted = !!formValues.generalParams.contractNumber

  const nameof = useNameof<InsuranceParamsTravelDto>()
  const phnameof = useNameof<PolicyHolderDto>(nameof(x => x.policyHolder))
  const trnameof = useNameof<TravelDto>(nameof(x => x.travel))
  const bpnameof = useNameof<BaseParamsDto>(nameof(x => x.baseParams))

  const { data: dataRange, isLoading: isLoadingRange, isFetching: isFetchingRange } = useGetRiskVariants(Risk.TRAVEL_RANGE)
  const { data: allInsurersData, isLoading: isLoadingInsurers, isFetching: isFetchingInsurers } = useInsurers(false, null)

  const methods = useForm<InsuranceParamsTravelDto>({
    defaultValues: formValues,
    resolver: yupResolver(schema) as any,
    mode: 'onBlur'
  })

  const allInsurers = useMemo(() => [...(allInsurersData ?? [])], [
    allInsurersData,
    methods.formState.defaultValues,
  ])

  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
    reset
  } = methods

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    if (triggerNewInsurance)
      reset(transformInsuranceParamsToFormValues(defaultValues))

    clearTriggerNewInsurance()
  }, [triggerNewInsurance])

  const { refetch: getBirthdate } = useGetBirthdate(getValues('policyHolder.person.birthNumber'), { enabled: false })

  const destination = watch('travel.destination')
  const phType = watch('policyHolder.person.type')

  const submitForm = handleSubmit((data) => {
    const values = _.cloneDeep(data)

    values.generalParams.insuranceStartDate = formatDate(values.generalParams.insuranceStartDate)
    values.policyHolder.person.birthday = formatDate(values.policyHolder.person.birthday)
    values.travel.insuranceEndDate = formatDate(values.travel.insuranceEndDate)
    values.travel.insurees?.forEach(x => x.birthdate = formatDate(x.birthdate))

    return onSubmit(values)
      .then(() => {
        if (values.nextPage == 'current') {
          savedFormAlertOnOpen()
          refetchInsuranceParams().then(({ data }) => {
            reset(transformInsuranceParamsToFormValues(data))
          })
        }
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join('. ') })
        })
      })
  })

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const insurees = watch('travel.insurees') ?? []

  const [isPolicyHolderInsureed, setIsPolicyHolderInsureed] = useState(insurees.findIndex(x => x.isPolicyHolder) >= 0)

  const firstname = watch('policyHolder.person.name')
  const surname = watch('policyHolder.person.surname')
  const birthdate = watch('policyHolder.person.birthday')
  const birthnumber = watch('policyHolder.person.birthNumber')
  const isForeigner = watch('policyHolder.person.isForeigner')
  const policyHolder = watch('policyHolder.person')
  const travelType = watch('travel.travelType')

  useEffect(() => {
    if (!(birthnumber && birthnumber.length >= 9 && birthnumber.length <= 10))
      return

    getBirthdate()
      .then(({ data }) => setValue('policyHolder.person.birthday', data?.data))
  }, [birthnumber, isForeigner]) // v pripade prepnuti z cizince na RC potrebuji prenastavit datum narozeni opet na zaklade RC

  useEffect(() => {
    const i = insurees.findIndex(x => !!x.isPolicyHolder)
    if (i >= 0) {
      insurees?.splice(i, 1)
      setValue('travel.insurees', [...insurees])
    }

    if (!isPolicyHolderInsureed)
      return

    setValue('travel.insurees', [
      ...insurees,
      {
        firstName: firstname,
        lastName: surname,
        birthdate: birthdate,
        birthnumber: isForeigner ? undefined : birthnumber,
        isPolicyHolder: true,
        isForeigner: isForeigner
      }])
  }, [firstname, surname, birthdate, birthnumber, isPolicyHolderInsureed, isForeigner, policyHolder])

  const focusTripEndDate = () => {
    tripEndDateRef.current?.focus()
  }

  const handleTripStartDateChange = (date?: Date) => {
    setValue('generalParams.insuranceStartDate', date)

    const isYearTravel = travelType === Selection.TravelTypeYearly || travelType === Selection.TravelTypeRepeated

    if (isYearTravel) {
      setValue('travel.insuranceEndDate', !date ? undefined : moment(date).add(1, 'years').add(-1, 'days').toDate())
    }
    else {
      setValue('travel.insuranceEndDate', !date ? undefined : moment(date).add(7, 'days').toDate())

      setTripEndDateOpened(true)
      focusTripEndDate()
    }
  }

  const insuranceStartDate = watch('generalParams.insuranceStartDate')
  const minStartDate = new Date().setHours(0, 0, 0, 0)

  const edOptions = useMemo(() => {
    if (travelType === Selection.TravelTypeYearly || travelType === Selection.TravelTypeRepeated) {
      const datePlusYear = moment(insuranceStartDate ?? new Date()).add(1, 'years').add(-1, 'days').toDate()
      return {
        minDate: datePlusYear,
        maxDate: datePlusYear,
        onClose: () => { setTripEndDateOpened(false) }
      }
    }
    else
      return {
        minDate: insuranceStartDate ?? undefined,
        onClose: () => { setTripEndDateOpened(false) }
      }
  }, [insuranceStartDate, travelType])

  useEffect(() => {
    if (travelType === Selection.TravelTypeYearly || travelType === Selection.TravelTypeRepeated) {
      setValue('travel.insuranceEndDate', !insuranceStartDate ? undefined : moment(insuranceStartDate).add(1, 'years').add(-1, 'days').toDate())
    }
  }, [travelType])

  const insuranceParamsTravel = watch()
  const { data: warnings } = useGetWarningsTravel()
  const formWarnings = getFormWarnings(insuranceParamsTravel, warnings)
  const insurerFormWarnings = formWarnings.filter(x => x.type === 'insurer')

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Parametry pojištění</FormSectionTitle>

              <FormSectionDescription>Zadejte základní parametry pojištění.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              {/* Project name */}
              <FormControl isInvalid={!!errors.generalParams?.name}>
                <FormLabel htmlFor="generalParams.name" optional>
                  Název nabídky
                </FormLabel>
                <InputGroup
                  id="generalParams.name"
                  isDisabled={isContracted}
                  placeholder="Zadejte název nabídky"
                  htmlSize={32}
                  errors={errors.generalParams?.name}
                  {...register('generalParams.name')}
                />
              </FormControl>

              <FormSpacer />

              <DestinationSelector />

              <FormSpacer />

              {destination && destination != Selection.TravelDestinationCzech &&
                <>
                  <TripType isContracted={isContracted} />
                  <FormSpacer />
                </>
              }


              <TripSportType isContracted={isContracted} />

              <FormSpacer />

              <TripKind isContracted={isContracted} />

              <FormSpacer />

              <FormGroup>
                {/* Date start */}
                <FormControl isInvalid={!!errors.generalParams?.insuranceStartDate} isRequired>
                  <FormLabel htmlFor="generalParams.insuranceStartDate">Datum počátku pojištění</FormLabel>
                  <Datepicker
                    ref={tripStartDateRef}
                    isDisabled={isContracted}
                    errors={errors.generalParams?.insuranceStartDate}
                    date={insuranceStartDate}
                    onDateChange={handleTripStartDateChange}
                    options={{
                      minDate: minStartDate,
                    }}
                  />
                </FormControl>

                {/* Date end */}
                <FormControl isInvalid={!!errors.travel?.insuranceEndDate} isRequired>
                  <FormLabel htmlFor="travel.insuranceEndDate">Datum konce pojištění</FormLabel>
                  <Datepicker
                    isDisabled={isContracted}
                    errors={errors.travel?.insuranceEndDate}
                    date={watch('travel.insuranceEndDate')}
                    name='insuranceEndDate'
                    ref={tripEndDateRef}
                    options={edOptions}
                    isOpen={tripEndDateOpened}
                    onClose={() => setTripEndDateOpened(false)}
                    onDateChange={(value) => {
                      setValue('travel.insuranceEndDate', value)
                    }}
                  />
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormControl isRequired isInvalid={!!errors.travel?.range}>
                <Stack direction={'row'}>
                  <FormLabel tooltip="Rozsah pojištění">Rozsah pojištění</FormLabel>
                  {(isFetchingRange || isLoadingRange) && <Spinner />}
                </Stack>
                <Controller
                  name="travel.range"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup onChange={e => onChange(parseInt(e))} value={value?.toString() ?? ''} isDisabled={isContracted}>
                      <RadioStack variant="stackedTile">
                        {dataRange?.filter(x => x.value != 'ALL').map((item) => (
                          <Radio value={item.id.toString()} key={item.id}>
                            {item.name}
                          </Radio>
                        ))}
                      </RadioStack>
                    </RadioGroup>
                  )}
                />
                {errors.travel?.range && <FormErrorMessage>{errors.travel.range.message}</FormErrorMessage>}
              </FormControl>

              <FormSpacer />

              <FormControl isInvalid={!!errors.baseParams?.insurers}>
                <InsuranceCompanies companies={allInsurers} isLoadingCompanies={(isFetchingInsurers || isLoadingInsurers)} propPath={bpnameof(x => x.insurers)} isDisabled={isContracted} />
                {errors.baseParams?.insurers && <FormErrorMessage>{errors.baseParams.insurers.message}</FormErrorMessage>}
              </FormControl>

              {insurerFormWarnings.length > 0 &&
                <Alert status='warning'>
                  <Stack direction="column">
                    {insurerFormWarnings.map((item) =>
                      <Text
                        key={item.message}
                        dangerouslySetInnerHTML={{ __html: item.message }}
                      />
                    )}
                  </Stack>
                </Alert>
              }
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Informace o pojistníkovi</FormSectionTitle>

              <FormSectionDescription>Zadejte informace o pojistníkovi.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <InsureeType personPrefix={phnameof(x => x.person)} />

              <FormSpacer />

              {isIn(phType, Selection.Person, Selection.SelfEmployed) && <BCNOptions personPrefix={phnameof(x => x.person)} />}
              {isIn(phType, Selection.Company, Selection.SelfEmployed) && <ICOOptions personPrefix={phnameof(x => x.person)} />}

              <FormGroup>
                {isIn(phType, Selection.Person, Selection.SelfEmployed) && <InsureeName personPrefix={phnameof(x => x.person)} showTitles isDisabled={isContracted} />}
              </FormGroup>

              <RegionSelector addressPrefix={phnameof(x => x.person.address)} isDisabled={isContracted} />

              {isIn(phType, Selection.Person, Selection.SelfEmployed) && insurees &&
                <>
                  <FormControl>
                    <SwitchControl>
                      <Switch
                        checked={isPolicyHolderInsureed}
                        defaultChecked={isPolicyHolderInsureed}
                        isDisabled={isContracted}
                        onChange={(e) => setIsPolicyHolderInsureed(e.target.checked)}
                      />
                      <FormLabel variant="switch" tooltip='Tuto možnost vyberte v případě, že je pojistník zároveň pojištěnou osobou'>
                        Je zároveň pojištěnou osobou
                      </FormLabel>
                    </SwitchControl>
                  </FormControl>

                  <FormSpacer />
                </>
              }

            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Pojištěné osoby</FormSectionTitle>

              <FormSectionDescription>Doplňte informace o dalších osobách.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <AdditionalInsurees prefix={nameof(x => x.travel.insurees)} editableBirthdate />
            </FormSectionBody>
          </FormSection>

          <ErrorSummary errors={errors} />

          {!isContracted &&
            <FormButtons
              rightButtons={
                <>
                  <Button variant="outline"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}>
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}>
                    <Check />
                    Uložit a pokračovat
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header='Uloženo'
                    body={'Údaje byly úspěšně uloženy'}
                  />
                </>
              }
            />
          }

        </MainContent>
      </form>
    </FormProvider>
  )
}
